import * as React from 'react';
import { useEffect, useState } from 'react';

/** Router Packages. */
import { useNavigate } from 'react-router-dom';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** Date & Time converting library. */
import dayjs from 'dayjs';

/** Internal Components */
import Main from '../layout/Main';
import { hasActiveRole } from 'shared/lib/roles';

export default function RenderComponent(props) {
    /** Get props from App.js.  */
    const {
        opdashboard,
        shipments,
        dataentry,
        dutycalculator,
        quotearchive,
        guideline,
        analytics,
        dataexport,
        accounting,
        ratemanagement,
        shipmentmonitoring,
        taskboard,
        clientsetting,
        performance,
        useractivity,
        myprofile,
    } = props;

    /** Get authentication status and user information. */
    const { isAuthenticated, user, logout } = useAuth0();

    /** Navigation handler */
    const navigate = useNavigate();

    /** User roles extracted from Auth0 */
    const userRole = user?.['https://drayage.insightshipping.io/roles'] ?? [0];

    /** State to track token expiration */
    const [isTokenExpired, setIsTokenExpired] = useState(false);

    /**
     * Check token expiry and set token expiry checker to True.
     * @param {Object} isTokenExpired - Token expiry state object.
     */
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        const checkTokenExpiry = () => {
            const tokenExpiry = sessionStorage.getItem('exp');
            if (tokenExpiry && new Date(tokenExpiry) < dayjs()) {
                setIsTokenExpired(true);
            }
        };
        const interval = setInterval(() => {
            checkTokenExpiry();
        }, 60000);
        return () => clearInterval(interval);
    }, []);

    /** Logout the user if the token is expired. */
    useEffect(() => {
        if (isTokenExpired) {
            logout({
                logoutParams: {
                    returnTo: window.location.origin,
                },
            });
        }
    }, [isTokenExpired]);

    /**
     * Send users who may not work in the system to the inactive page.
     * A user given the Inactive role is sent there even if older roles remain
     * on the account. Returns true when the user was redirected.
     */
    const restrictAccess = (userRole) => {
        if (!hasActiveRole(userRole)) {
            navigate('/inactive');
            return true;
        }
        return false;
    };

    /** Restrict access except for staff. */
    const restrictStaff = (userRole) => {
        restrictAccess(userRole);
    };

    /** Restrict access except for manager. Administrator always has access. */
    const restrictManager = (userRole, allowAssistant = false) => {
        if (restrictAccess(userRole)) {
            return;
        }
        if (userRole.includes('Administrator')) {
            return;
        }
        if (userRole.includes('Operation-Staff')) {
            return navigate('/opdashboard');
        }
        if (userRole.includes('Operation-Assistant Manager') && !allowAssistant) {
            return navigate('/opdashboard');
        }
    };

    /** Restrict access except for administrator. */
    const restrictAdmin = (userRole) => {
        if (restrictAccess(userRole)) {
            return;
        }
        if (!userRole.includes('Administrator')) {
            return navigate('/opdashboard');
        }
    };

    /** Effect to handle navigation based on props and userRole. */
    useEffect(() => {
        const handleNavigation = () => {
            switch (true) {
                case opdashboard:
                case shipments:
                case dataentry:
                case dutycalculator:
                case quotearchive:
                case guideline:
                case analytics:
                case dataexport:
                case accounting:
                    restrictStaff(userRole);
                    break;
                case ratemanagement:
                    restrictManager(userRole, true);
                    break;
                case shipmentmonitoring:
                    restrictManager(userRole, false);
                    break;
                case taskboard:
                    restrictManager(userRole, true);
                    break;
                case clientsetting:
                case performance:
                    restrictManager(userRole, false);
                    break;
                case useractivity:
                    restrictAdmin(userRole);
                    break;
                case myprofile:
                    restrictAccess(userRole);
                    break;
                default:
                    break;
            }
        };
        handleNavigation();
    }, [
        userRole,
        opdashboard,
        shipments,
        dataentry,
        dutycalculator,
        quotearchive,
        guideline,
        analytics,
        dataexport,
        accounting,
        ratemanagement,
        shipmentmonitoring,
        taskboard,
        clientsetting,
        performance,
        useractivity,
        myprofile,
        isAuthenticated,
    ]);

    return (
        <Main
            userRole={userRole}
            opdashboard={opdashboard}
            shipments={shipments}
            dataentry={dataentry}
            dutycalculator={dutycalculator}
            quotearchive={quotearchive}
            guideline={guideline}
            analytics={analytics}
            dataexport={dataexport}
            accounting={accounting}
            ratemanagement={ratemanagement}
            shipmentmonitoring={shipmentmonitoring}
            taskboard={taskboard}
            clientsetting={clientsetting}
            performance={performance}
            useractivity={useractivity}
            myprofile={myprofile}
        />
    );
}
